import React from 'react';
import { Link } from 'react-router-dom';
import {
  Header, Segment, Button, List,
} from 'semantic-ui-react';
import ProfileModal from '../../Experts/ProfileModal';
import TranscriptModal from './TranscriptModal';

// Most quotes are one AI-selected sentence, but a citation without a literal
// quote to anchor to falls back to its entire retrieved source chunk (up to
// a few thousand characters) — truncate that display so the column doesn't
// grow to the length of the chunk; "View transcript" still opens the full
// source with the untruncated quote used to locate and highlight it.
const MAX_QUOTE_DISPLAY_LENGTH = 400;

const truncateForDisplay = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  const cutoff = text.lastIndexOf(' ', maxLength);
  return `${text.slice(0, cutoff > 0 ? cutoff : maxLength)}…`;
};

// `projectId` is the project the panel is shown within (project chat, AI
// Report). It's absent on the team-wide /ai-chat page, where each reference
// carries its own project instead. `showTeam` also names the project's team,
// for global chat, whose references span every team.
const ReferencePanel = ({ projectId, reference, showTeam }) => {
  if (!reference) {
    return (
      <Segment placeholder>
        <p>Click a reference marker to see its source details here.</p>
      </Segment>
    );
  }

  // A job's source data also includes non-transcript content (project info,
  // screening/qualification responses, participant list, post-engagement
  // rating) alongside its actual interview transcript — that content isn't a
  // spoken quote and "View transcript" can't locate it in a recording, so it
  // gets a plainer "source excerpt" treatment instead of quote styling.
  // Expert profiles (global chat only) aren't tied to any project or transcript.
  const isNonTranscriptExcerpt = reference.sourceType === 'other' || reference.sourceType === 'expert_profile';
  const excerptHeader = reference.sourceType === 'expert_profile' ? 'Expert profile excerpt' : 'Source excerpt';
  const referenceProjectId = reference.projectId || projectId;
  // Only worth showing when the panel isn't already within that same project.
  const showProject = reference.project && reference.project.id !== projectId;

  return (
    <Segment>
      <Header as="h5">{reference.label || `Reference ${reference.id}`}</Header>
      {reference.quote && (
        isNonTranscriptExcerpt ? (
          <>
            <Header as="h6">{excerptHeader}</Header>
            <p style={{ whiteSpace: 'pre-wrap' }}>{truncateForDisplay(reference.quote, MAX_QUOTE_DISPLAY_LENGTH)}</p>
          </>
        ) : (
          <blockquote style={{ fontStyle: 'italic', marginLeft: 0 }}>
            {`“${truncateForDisplay(reference.quote, MAX_QUOTE_DISPLAY_LENGTH)}”`}
          </blockquote>
        )
      )}
      {showProject && (
        <>
          <Header as="h6">Project</Header>
          <List>
            <List.Item>
              <Link to={`/projects/${reference.project.id}`}>{reference.project.title}</Link>
            </List.Item>
            {showTeam && reference.project.team?.name && (
              <List.Item>{`Team: ${reference.project.team.name}`}</List.Item>
            )}
          </List>
        </>
      )}
      {reference.expert && (
        <>
          <Header as="h6">Expert</Header>
          <List>
            <List.Item>{reference.expert.fullName}</List.Item>
            {reference.expert.title && <List.Item>{reference.expert.title}</List.Item>}
            {reference.expert.organization?.name && (
              <List.Item>{reference.expert.organization.name}</List.Item>
            )}
            {reference.expert.country && <List.Item>{reference.expert.country}</List.Item>}
          </List>
          <ProfileModal expertId={reference.expertId}>
            <Button size="tiny" style={{ marginTop: '6px' }}>View full profile</Button>
          </ProfileModal>
        </>
      )}
      {reference.jobId && referenceProjectId && !isNonTranscriptExcerpt && (
        <TranscriptModal
          projectId={referenceProjectId}
          jobId={reference.jobId}
          quote={reference.quote}
        >
          <Button size="tiny" style={{ marginTop: '6px' }}>View transcript</Button>
        </TranscriptModal>
      )}
    </Segment>
  );
};

export default ReferencePanel;
